import {
  createSimulatorBridgeErrorResult,
  createSimulatorBridgeSuccessResult,
  sanitizeSimulatorBridgeResponse,
  type SimulatorBridgeCommandResult
} from "../shared/domain/simulatorBridge.js";

export type SimulatorNodeType = "client" | "server" | "router" | "switch" | "firewall";

export type SimulatorTopologyNode = {
  id: string;
  label: string;
  nodeType: SimulatorNodeType;
  roles: string[];
  legacyMachineKey: "CLIENT01" | "SRV01" | string;
  ip?: string;
};

export type SimulatorTopology = {
  nodes: SimulatorTopologyNode[];
  networks: Array<{
    id: string;
    cidr: string;
    vlan?: number;
    gateway?: string;
  }>;
  sharedServices: {
    directory?: {
      domain: string;
      domainControllers: string[];
    };
    dns?: {
      servers: string[];
      zones: string[];
    };
    dhcp?: {
      servers: string[];
      scopes: string[];
    };
    gpo?: {
      scope: "domain" | "site" | "ou";
      servers: string[];
    };
  };
};

export const defaultSimulatorTopology: SimulatorTopology = {
  nodes: [
    {
      id: "CLIENT01",
      label: "CLIENT01",
      nodeType: "client",
      roles: ["workstation"],
      legacyMachineKey: "CLIENT01",
      ip: "192.168.10.20"
    },
    {
      id: "CLIENT02",
      label: "CLIENT02",
      nodeType: "client",
      roles: ["workstation"],
      legacyMachineKey: "CLIENT01",
      ip: "192.168.10.22"
    },
    {
      id: "SRV01",
      label: "SRV01",
      nodeType: "server",
      roles: ["domainController", "dnsServer", "dhcpServer"],
      legacyMachineKey: "SRV01",
      ip: "192.168.10.10"
    },
    {
      id: "SRV02",
      label: "SRV02",
      nodeType: "server",
      roles: ["memberServer"],
      legacyMachineKey: "SRV01",
      ip: "192.168.10.11"
    },
    {
      id: "RTR01",
      label: "RTR01",
      nodeType: "router",
      roles: ["gateway"],
      legacyMachineKey: "CLIENT01",
      ip: "192.168.10.1"
    }
  ],
  networks: [
    {
      id: "lan-10",
      cidr: "192.168.10.0/24",
      vlan: 10,
      gateway: "192.168.10.1"
    }
  ],
  sharedServices: {
    directory: {
      domain: "advanced-ti.local",
      domainControllers: ["SRV01"]
    },
    dns: {
      servers: ["SRV01"],
      zones: ["advanced-ti.local"]
    },
    dhcp: {
      servers: ["SRV01"],
      scopes: ["192.168.10.0/24"]
    },
    gpo: {
      scope: "domain",
      servers: ["SRV01"]
    }
  }
};

export const legacyV1ServerOnlyAppIds = [
  "serverManager",
  "aduc",
  "gpmc",
  "dnsManager",
  "dhcpManager"
] as const;

export function sanitizeSimulatorNodeId(value: string): string {
  const sanitized = value.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "-");
  return sanitized.length > 0 ? sanitized : "CLIENT01";
}

export function buildSimulatorStorageKey(input: { attemptId: string; nodeId: string }): string {
  return [
    "firstdesk-v2-simulator",
    sanitizeSimulatorNodeId(input.attemptId),
    sanitizeSimulatorNodeId(input.nodeId)
  ].join("-");
}

export function nodeForSimulatorId(nodeId: string, topology: SimulatorTopology = defaultSimulatorTopology): SimulatorTopologyNode {
  const normalized = sanitizeSimulatorNodeId(nodeId);
  return topology.nodes.find((node) => sanitizeSimulatorNodeId(node.id) === normalized) ?? topology.nodes[0];
}

export function simulatorTopologyForTicket(input: {
  environment?: string | null;
  nodes?: SimulatorTopologyNode[] | null;
}): SimulatorTopology {
  const explicitNodes = Array.isArray(input.nodes)
    ? input.nodes.filter((node) => node.id.trim().length > 0)
    : [];
  if (explicitNodes.length > 0) {
    return {
      ...defaultSimulatorTopology,
      nodes: explicitNodes
    };
  }

  const environment = String(input.environment ?? "").trim().toLowerCase();
  const nodeIds = (() => {
    if (environment === "server") return ["SRV01"];
    if (environment === "domain" || environment === "hybrid") return ["CLIENT01", "SRV01"];
    if (environment === "entra") return ["CLIENT01"];
    return ["CLIENT01"];
  })();

  return {
    ...defaultSimulatorTopology,
    nodes: defaultSimulatorTopology.nodes.filter((node) => nodeIds.includes(node.id))
  };
}

export function buildScenarioSimulatorUrl(input: {
  simulatorStorageKey: string;
  machine: string;
  ticketId: string;
  nodeId?: string;
  nodeType?: SimulatorNodeType;
  runtimePath?: string;
  attemptId?: string | null;
}): string {
  const nodeId = sanitizeSimulatorNodeId(input.nodeId ?? input.machine);
  const params = new URLSearchParams({
    fdStorageKey: input.simulatorStorageKey,
    machine: input.machine,
    nodeId,
    nodeType: input.nodeType ?? (input.machine === "SRV01" ? "server" : "client"),
    ticket: input.ticketId,
    mode: "scenario"
  });

  if (input.attemptId && input.attemptId.trim().length > 0) {
    params.set("attemptId", input.attemptId.trim());
  }

  const runtimePath = input.runtimePath ?? "/simulator-v1/index.html";
  return `${runtimePath}?${params.toString()}`;
}

export function resolveSimulatorBridgeAttemptId(
  ticketAttemptId: string | null | undefined,
  bridgeAttemptId?: string | number
): string | null {
  const ticketId = String(ticketAttemptId ?? "").trim();
  if (ticketId.length > 0) {
    return ticketId;
  }

  const bridgeId = String(bridgeAttemptId ?? "").trim();
  return bridgeId.length > 0 ? bridgeId : null;
}

export function isTrustedSimulatorBridgeEvent(input: {
  eventOrigin: string;
  expectedOrigin: string;
  eventSource: MessageEventSource | null;
  iframeWindow: Window | null;
}): boolean {
  return Boolean(input.iframeWindow)
    && input.eventSource === input.iframeWindow
    && input.eventOrigin === input.expectedOrigin;
}

function readBridgeErrorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object" && "message" in body && typeof body.message === "string" && body.message.trim().length > 0) {
    return body.message.trim();
  }

  if (body && typeof body === "object" && "error" in body && typeof body.error === "string" && body.error.trim().length > 0) {
    return body.error.trim();
  }

  return fallback;
}

export function createBridgeResultFromApi(input: {
  requestId?: string;
  ok: boolean;
  status: number;
  body?: unknown;
}): SimulatorBridgeCommandResult {
  if (!input.ok) {
    return createSimulatorBridgeErrorResult(
      readBridgeErrorMessage(input.body, `bridge_command_failed_${input.status}`),
      input.requestId
    );
  }

  const sanitized = sanitizeSimulatorBridgeResponse(input.body);
  return createSimulatorBridgeSuccessResult({
    requestId: input.requestId,
    commandId: sanitized.commandId,
    result: sanitized.result
  });
}
